<template>
    <div class="max-w-[80%] rounded-lg border border-emerald-200 bg-white shadow-sm" data-testid="role-play-summary-card">
        <div class="flex items-center justify-between border-b border-emerald-100 bg-emerald-50 px-4 py-2">
            <div class="flex items-center gap-2">
                <svg class="h-4 w-4 text-emerald-700" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <h3 class="text-sm font-semibold text-emerald-900">{{ data.summary.title }}</h3>
            </div>
            <span class="text-[11px] text-emerald-700">with {{ data.persona_name }}</span>
        </div>

        <div class="space-y-3 px-4 py-3 text-sm">
            <p class="text-slate-700">{{ data.summary.overall_summary }}</p>

            <div v-if="data.summary.strengths.length" class="space-y-1">
                <h4 class="text-xs font-semibold uppercase tracking-wide text-emerald-700">Strengths</h4>
                <ul class="space-y-0.5">
                    <!-- eslint-disable-next-line vue/no-v-html — strengths/weaknesses are LLM-emitted markdown bold spans, no user HTML -->
                    <li v-for="(s, i) in data.summary.strengths" :key="i" class="text-xs text-slate-700" v-html="formatBold(s)" />
                </ul>
            </div>

            <div v-if="data.summary.weaknesses.length" class="space-y-1">
                <h4 class="text-xs font-semibold uppercase tracking-wide text-amber-700">Areas to grow</h4>
                <ul class="space-y-0.5">
                    <!-- eslint-disable-next-line vue/no-v-html — see above -->
                    <li v-for="(w, i) in data.summary.weaknesses" :key="i" class="text-xs text-slate-700" v-html="formatBold(w)" />
                </ul>
            </div>

            <div v-if="data.skills.length" class="space-y-2">
                <h4 class="text-xs font-semibold uppercase tracking-wide text-slate-600">Skill scorecard</h4>
                <ul class="divide-y divide-slate-100 rounded border border-slate-200">
                    <li v-for="skill in data.skills" :key="skill.name" class="px-3 py-2">
                        <div class="flex items-center justify-between gap-2">
                            <span class="text-xs font-medium text-slate-800">{{ skill.name }}</span>
                            <span :class="scoreBadgeClass(skill.score)" class="rounded-full px-2 py-0.5 text-[10px] font-medium">{{ skill.score }} / 5</span>
                        </div>
                        <p class="mt-1 text-[11px] text-slate-600">{{ skill.rationale }}</p>
                        <p v-if="skill.how_to_improve" class="mt-0.5 text-[11px] italic text-slate-500">→ {{ skill.how_to_improve }}</p>
                    </li>
                </ul>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

import type { RolePlaySummaryResult } from "../types";

const props = defineProps<{ result: RolePlaySummaryResult; toolName: string }>();
const data = computed<RolePlaySummaryResult>(() => props.result);

const escapeHtml = (s: string): string =>
    s.replace(/[&<>"']/g, (ch) => {
        switch (ch) {
            case "&":
                return "&amp;";
            case "<":
                return "&lt;";
            case ">":
                return "&gt;";
            case '"':
                return "&quot;";
            default:
                return "&#39;";
        }
    });

// Convert `**text**` markdown bold to <strong>text</strong>. The LLM
// produces strengths/weaknesses in this exact shape; everything else is
// HTML-escaped first so we don't render arbitrary markup.
const formatBold = (text: string): string => {
    return escapeHtml(text).replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
};

const scoreBadgeClass = (score: number): string => {
    if (score >= 4) {
        return "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200";
    }
    if (score >= 3) {
        return "bg-sky-50 text-sky-800 ring-1 ring-sky-200";
    }
    return "bg-amber-50 text-amber-800 ring-1 ring-amber-200";
};
</script>
